// src/admin/pages/tabs/CapRaiseRoundsTab.js
import React, { useState, useEffect } from 'react';
import { DollarSign, User, TrendingUp, Calendar } from 'lucide-react';
import { supabase } from '../../../config';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const day = date.getDate();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

const CapRaiseRoundsTab = () => {
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRounds();
  }, []);

  const loadRounds = async () => {
    try {
      setLoading(true);
      
      // Fetch all capraise rounds
      const { data: roundsData, error: roundsError } = await supabase
        .from('capraise_rounds')
        .select('*')
        .order('created_at', { ascending: false });

      if (roundsError) throw roundsError;

      // Fetch user profiles separately
      const userIds = [...new Set((roundsData || []).map(r => r.user_id).filter(Boolean))];
      let userProfilesMap = {};
      
      if (userIds.length > 0) {
        const { data: profilesData } = await supabase
          .from('user_profiles')
          .select('id, first_name, last_name, email')
          .in('id', userIds);
        
        if (profilesData) {
          userProfilesMap = profilesData.reduce((acc, profile) => {
            acc[profile.id] = profile;
            return acc;
          }, {});
        }
      }

      // Fetch companies separately
      const companyIds = [...new Set((roundsData || []).map(r => r.company_id).filter(Boolean))];
      let companiesMap = {};
      
      if (companyIds.length > 0) {
        const { data: companiesData } = await supabase
          .from('equity_companies')
          .select('id, name')
          .in('id', companyIds);
        
        if (companiesData) {
          companiesMap = companiesData.reduce((acc, company) => {
            acc[company.id] = company;
            return acc;
          }, {});
        }
      }

      // Attach creator and company info to rounds
      const roundsWithDetails = (roundsData || []).map(round => ({
        ...round,
        creator: round.user_id ? userProfilesMap[round.user_id] : null,
        company: round.company_id ? companiesMap[round.company_id] : null
      }));

      setRounds(roundsWithDetails);
    } catch (error) {
      console.error('Error loading rounds:', error);
      setRounds([]);
    } finally {
      setLoading(false);
    }
  };

  const getCreatorName = (round) => {
    if (round.creator) {
      if (round.creator.first_name && round.creator.last_name) {
        return `${round.creator.first_name} ${round.creator.last_name}`;
      }
      return round.creator.first_name || round.creator.last_name || round.creator.email || 'Unknown';
    }
    return 'Unknown';
  };

  const getCompanyName = (round) => {
    if (round.company) {
      return round.company.name || 'Unknown Company';
    }
    return 'Unknown Company';
  };

  const formatCurrency = (value) => {
    if (!value || value === 0) return '$0';
    const num = parseFloat(value);
    if (isNaN(num)) return '$0';
    
    if (num >= 1000000) {
      return `$${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `$${(num / 1000).toFixed(1)}K`;
    } else {
      return `$${num.toFixed(0)}`;
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading rounds...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">All CapRaise Rounds</h2>
          <p className="text-gray-600 mt-1">View all capital raising rounds</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <DollarSign className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Rounds</p>
              <p className="text-2xl font-bold text-gray-900">{rounds.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <TrendingUp className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Target</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency((Array.isArray(rounds) ? rounds : []).reduce((sum, r) => sum + (parseFloat(r.target_amount) || 0), 0))}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <TrendingUp className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Valuation</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency((Array.isArray(rounds) ? rounds : []).reduce((sum, r) => sum + (parseFloat(r.pre_money_valuation || r.post_money_valuation) || 0), 0))}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <User className="h-8 w-8 text-orange-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Unique Creators</p>
              <p className="text-2xl font-bold text-gray-900">
                {new Set((Array.isArray(rounds) ? rounds : []).map(r => r.user_id)).size}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Rounds Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Round Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Creator
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Size
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valuation
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Array.isArray(rounds) && rounds.map((round) => (
                <tr key={round.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <DollarSign className="h-5 w-5 text-blue-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {round.round_name || 'Unnamed Round'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {getCompanyName(round)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <a 
                      href={`/adminhq/users?user=${round.user_id}`}
                      className="flex items-center text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      <User className="h-4 w-4 text-gray-400 mr-1" />
                      {getCreatorName(round)}
                    </a>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {formatCurrency(round.target_amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(round.pre_money_valuation || round.post_money_valuation)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {round.round_type || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                      {round.created_at 
                        ? formatDate(round.created_at)
                        : 'N/A'}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {rounds.length === 0 && (
          <div className="text-center py-12">
            <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No rounds</h3>
            <p className="mt-1 text-sm text-gray-500">No capital raising rounds have been created yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CapRaiseRoundsTab;

